import { useQuery } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import ArrowLeftIcon from '@app/assets/icons/arrow-left.svg?react';
import { Button } from '@app/components/Button/Button';
import { ErrorLogInterface } from '@app/models/log/error';
import {
  getErrorLogTypeColor,
  getErrorLogLevelColor,
} from '@app/constants/error-log';
import { getErrorLogById } from '@app/services/admin';

export const ErrorLogDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const {
    data: errorLog,
    isLoading,
    isError,
  } = useQuery<ErrorLogInterface>({
    queryKey: ['logs', 'error', id],
    queryFn: () => getErrorLogById(id!),
    enabled: !!id,
  });

  const handleGoBack = () => {
    navigate('/admin/logs/error');
  };

  const backButton = (
    <Button
      onClick={handleGoBack}
      kind="tertiary"
      className="flex items-center gap-2"
    >
      <ArrowLeftIcon className="w-4 h-4" />
      Back to Error Logs
    </Button>
  );

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">{backButton}</div>
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded-md w-1/3"></div>
          <div className="h-4 bg-gray-200 rounded-md w-1/2"></div>
          <div className="h-32 bg-gray-200 rounded-md"></div>
        </div>
      </div>
    );
  }

  if (isError || !errorLog) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">{backButton}</div>
        <div className="p-4 bg-red-50 text-red-700 rounded-md">
          Error loading error log details. Please try again later.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">{backButton}</div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-start justify-between">
            <div className="space-y-2">
              <h1 className="text-2xl font-bold text-gray-900">
                Error Log Details
              </h1>
              <p className="text-sm text-gray-500">
                ID: {errorLog.id} •{' '}
                {format(new Date(errorLog.createdAt), 'PPpp')}
              </p>
            </div>
            <div className="flex flex-col gap-2 items-end">
              <span
                className={`px-3 py-1 inline-flex text-sm leading-5 font-semibold rounded-full ${getErrorLogLevelColor(errorLog.level)}`}
              >
                {errorLog.level}
              </span>
              <span
                className={`px-3 py-1 inline-flex text-sm leading-5 font-semibold rounded-full ${getErrorLogTypeColor(errorLog.type)}`}
              >
                {errorLog.type.replace('_', ' ')}
              </span>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Error Message */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Error Message
            </h3>
            <div className="p-4 bg-red-50 border border-red-200 rounded-md">
              <p className="text-red-800 font-medium">{errorLog.message}</p>
            </div>
          </div>

          {/* User and Business Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">User</h3>
              <div className="p-4 bg-gray-50 rounded-md">
                {errorLog.user ? (
                  <div className="space-y-1">
                    <p className="font-medium text-gray-900">
                      {errorLog.user.firstName} {errorLog.user.lastName}
                    </p>
                    <p className="text-sm text-gray-600">
                      {errorLog.user.email}
                    </p>
                    <p className="text-xs text-gray-500">
                      ID: {errorLog.userId}
                    </p>
                  </div>
                ) : (
                  <p className="text-gray-500">Unknown User</p>
                )}
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Business
              </h3>
              <div className="p-4 bg-gray-50 rounded-md">
                {errorLog.business ? (
                  <div className="space-y-1">
                    <p className="font-medium text-gray-900">
                      {errorLog.business.name}
                    </p>
                    <p className="text-sm text-gray-600">
                      {errorLog.business.email}
                    </p>
                    <p className="text-xs text-gray-500">
                      ID: {errorLog.businessId}
                    </p>
                  </div>
                ) : (
                  <p className="text-gray-500">Unknown Business</p>
                )}
              </div>
            </div>
          </div>

          {/* Integration Type */}
          {errorLog.integrationType && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Integration
              </h3>
              <div className="p-4 bg-gray-50 rounded-md">
                <p className="text-gray-900">{errorLog.integrationType}</p>
              </div>
            </div>
          )}

          {/* Metadata */}
          {errorLog.metadata && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Request Metadata
              </h3>
              <div className="p-4 bg-gray-50 rounded-md">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {errorLog.metadata.code && (
                    <div>
                      <p className="text-sm font-medium text-gray-700">
                        Status Code
                      </p>
                      <p className="text-gray-900">{errorLog.metadata.code}</p>
                    </div>
                  )}
                  {errorLog.metadata.method && (
                    <div>
                      <p className="text-sm font-medium text-gray-700">
                        HTTP Method
                      </p>
                      <p className="text-gray-900">
                        {errorLog.metadata.method}
                      </p>
                    </div>
                  )}
                  {errorLog.metadata.url && (
                    <div>
                      <p className="text-sm font-medium text-gray-700">URL</p>
                      <p className="text-gray-900 break-all">
                        {errorLog.metadata.url}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Stack Trace
            </h3>
            <div className="p-4 bg-gray-900 rounded-md overflow-x-auto">
              <pre className="text-sm text-gray-100 font-mono whitespace-pre-wrap">
                {errorLog.stackTrace}
              </pre>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ErrorLogDetail;
